/**
 * Customer Type Switcher JavaScript
 * Handles persistence (localStorage, cookies, cart attributes), DOM updates, and events
 * To remove: delete this file and remove the script tag from theme.liquid or header.liquid
 */

class CustomerTypeSwitcher {
  constructor() {
    this.initialized = false;
    this.debounceTimeout = null;
    this.cookieName = 'customer_type';
    this.localStorageKey = 'customer_type';
    this.cartAttributeKey = 'customer_type';
    this.popupShownKey = 'customer_type_popup_shown';
    
    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.debouncedCartUpdate = this.debouncedCartUpdate.bind(this);
    this.handlePopupClick = this.handlePopupClick.bind(this);
    
    this.init();
  }
  
  init() {
    if (this.initialized) return;
    
    // Early setup to prevent layout shift
    this.setInitialState();
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupSwitcher();
        this.checkAndShowPopup();
      });
    } else {
      this.setupSwitcher();
      this.checkAndShowPopup();
    }
    
    this.initialized = true;
  }
  
  setInitialState() {
    try {
      const initialValue = this.resolveInitialValue();
      this.updateDOM(initialValue, false);
    } catch (error) {
      console.warn('Customer Type Switcher: Failed to set initial state', error);
      this.updateDOM('private', false);
    }
  }
  
  resolveInitialValue() {
    // Priority: URL param → localStorage → cookie → cart attribute → fallback
    
    // 1. URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlValue = urlParams.get('customer_type');
    if (this.isValidValue(urlValue)) {
      return urlValue;
    }
    
    // 2. localStorage (if enabled)
    try {
      const localValue = localStorage.getItem(this.localStorageKey);
      if (this.isValidValue(localValue)) {
        return localValue;
      }
    } catch (e) {
      console.warn('Customer Type Switcher: localStorage not available');
    }
    
    // 3. Cookie
    const cookieValue = this.getCookie(this.cookieName);
    if (this.isValidValue(cookieValue)) {
      return cookieValue;
    }
    
    // 4. Cart attribute (will be resolved via fetch later)
    // For now, return fallback
    return 'private';
  }
  
  async setupSwitcher() {
    const switchers = document.querySelectorAll('.cts-switcher');
    
    if (switchers.length === 0) return;
    
    // Resolve cart attribute value
    const cartValue = await this.getCartAttribute();
    const finalValue = this.resolveInitialValue();
    
    // If cart has different value, use that
    const resolvedValue = this.isValidValue(cartValue) ? cartValue : finalValue;
    
    // Update all switchers
    switchers.forEach(switcher => {
      this.setupSwitcherElement(switcher, resolvedValue);
    });
    
    // Ensure cart attribute is set to match resolved value
    // This ensures prices display correctly on page load
    if (!cartValue || cartValue !== resolvedValue) {
      await this.updateCartAttribute(resolvedValue);
    }
    
    // Update persistence if URL param was used
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('customer_type')) {
      this.persistValue(resolvedValue, false);
    }
  }
  
  setupSwitcherElement(switcher, initialValue) {
    const buttons = switcher.querySelectorAll('.cts-button');
    const clientStorage = switcher.dataset.clientStorage !== 'false';
    
    // Update buttons state
    this.updateButtonsState(buttons, initialValue);
    
    // Add event listeners
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        this.handleClick(e, switcher, clientStorage);
      });
    });
  }
  
  async handleClick(event, switcher, clientStorage) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const value = button.dataset.value;
    
    if (!this.isValidValue(value)) return;
    
    const buttons = switcher.querySelectorAll('.cts-button');
    
    // Update UI immediately
    this.updateButtonsState(buttons, value);
    
    // Persist the value (including cart attribute)
    await this.persistValue(value, clientStorage);
    
    // Update DOM attributes
    this.updateDOM(value, true);
    
    // Dispatch event and reload
    this.dispatchChangeEvent(value);
  }
  
  updateButtonsState(buttons, activeValue) {
    buttons.forEach(button => {
      const isActive = button.dataset.value === activeValue;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      
      // Update CSS classes for visual state
      if (isActive) {
        button.classList.add('cts-button--active');
      } else {
        button.classList.remove('cts-button--active');
      }
    });
  }
  
  async persistValue(value, clientStorage) {
    try {
      // localStorage (if enabled)
      if (clientStorage) {
        try {
          localStorage.setItem(this.localStorageKey, value);
        } catch (e) {
          console.warn('Customer Type Switcher: localStorage not available');
        }
        
        // Cookie (7 days)
        this.setCookie(this.cookieName, value, 7);
      }
      
      // Update cart attribute immediately (await completion)
      await this.updateCartAttribute(value);
      
    } catch (error) {
      console.error('Customer Type Switcher: Failed to persist value', error);
    }
  }
  
  debouncedCartUpdate(value) {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    
    this.debounceTimeout = setTimeout(() => {
      this.updateCartAttribute(value);
    }, 300);
  }
  
  async updateCartAttribute(value) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          attributes: {
            [this.cartAttributeKey]: value
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status}`);
      }
      
    } catch (error) {
      console.error('Customer Type Switcher: Failed to update cart', error);
    }
  }
  
  async getCartAttribute() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      
      const cart = await response.json();
      return cart.attributes && cart.attributes[this.cartAttributeKey] || null;
      
    } catch (error) {
      console.warn('Customer Type Switcher: Failed to fetch cart', error);
      return null;
    }
  }
  
  updateDOM(value, dispatchEvent = false) {
    // Update html data attribute
    document.documentElement.setAttribute('data-customer-type', value);
    
    // Update body class
    document.body.classList.remove('customer-type--private', 'customer-type--business');
    document.body.classList.add(`customer-type--${value}`);
  }
  
  dispatchChangeEvent(value) {
    const event = new CustomEvent('customerType:change', {
      detail: { value },
      bubbles: true
    });
    
    document.dispatchEvent(event);
    
    // Reload page immediately - cart attribute is already updated
    window.location.reload();
  }
  

  
  isValidValue(value) {
    return ['private', 'business'].includes(value);
  }
  
  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }
  
  getCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    
    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }
    return null;
  }
  
  checkAndShowPopup() {
    // Check if popup has been shown before
    try {
      const popupShown = localStorage.getItem(this.popupShownKey);
      if (popupShown === 'true') {
        return; // Don't show popup if already shown
      }
    } catch (e) {
      // If localStorage is not available, check cookie
      const popupShown = this.getCookie(this.popupShownKey);
      if (popupShown === 'true') {
        return;
      }
    }
    
    // Check if customer type is already set
    const currentType = this.resolveInitialValue();
    const hasExistingChoice = this.getCookie(this.cookieName) || 
                              (this.localStorageAvailable() && localStorage.getItem(this.localStorageKey));
    
    if (hasExistingChoice) {
      this.markPopupAsShown();
      return;
    }
    
    // Show popup
    this.showPopup();
  }
  
  showPopup() {
    const popup = document.getElementById('customer-type-popup');
    if (!popup) return;
    
    // Show popup
    popup.classList.add('is-visible');
    
    // Disable body scroll
    document.body.style.overflow = 'hidden';
    
    // Setup button listeners
    const buttons = popup.querySelectorAll('.cts-popup__button');
    buttons.forEach(button => {
      button.addEventListener('click', this.handlePopupClick);
    });
    
    // Close on overlay click
    const overlay = popup.querySelector('.cts-popup__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => {
        // Default to private if user closes without selecting
        this.handlePopupSelection('private');
      });
    }
    
    // Close on Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        this.handlePopupSelection('private');
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  }
  
  handlePopupClick(event) {
    const button = event.currentTarget;
    const customerType = button.dataset.customerType;
    
    if (this.isValidValue(customerType)) {
      this.handlePopupSelection(customerType);
    }
  }
  
  handlePopupSelection(customerType) {
    // Save the selection
    this.persistValue(customerType, true);
    this.updateDOM(customerType, false);
    
    // Mark popup as shown
    this.markPopupAsShown();
    
    // Hide popup
    this.hidePopup();
    
    // Reload page to show correct prices
    setTimeout(() => {
      window.location.reload();
    }, 300);
  }
  
  hidePopup() {
    const popup = document.getElementById('customer-type-popup');
    if (!popup) return;
    
    popup.classList.remove('is-visible');
    document.body.style.overflow = '';
  }
  
  markPopupAsShown() {
    try {
      localStorage.setItem(this.popupShownKey, 'true');
    } catch (e) {
      // Fallback to cookie
      this.setCookie(this.popupShownKey, 'true', 365);
    }
  }
  
  localStorageAvailable() {
    try {
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }
}

// Auto-initialize when script loads
if (typeof window !== 'undefined') {
  window.CustomerTypeSwitcher = CustomerTypeSwitcher;
  
  // Create instance
  new CustomerTypeSwitcher();
}